import { useCallback, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

/**
 * Frecce, Home e End dentro una `role="tablist"`, come vuole il pattern ARIA.
 *
 * La selezione segue il fuoco (*automatic activation*): tutte le schermate che
 * lo usano tengono i pannelli montati insieme, quindi spostarsi non costa
 * niente e chi naviga da tastiera vede il contenuto mentre scorre, invece di
 * doverlo confermare con Invio.
 *
 * Vive qui e non dentro una pagina perche' lo vogliono identico in tre punti —
 * il dettaglio dell'arena, il dettaglio del torneo e la lobby — e tre copie
 * dello stesso ciclo con l'indice a mano sono tre occasioni di sbagliare il
 * modulo sul primo o sull'ultimo.
 *
 * @param chiavi le schede, nell'ordine in cui stanno sullo schermo
 * @param attiva la scheda selezionata adesso
 * @param cambia cosa fare quando la selezione si sposta
 */
export function useTablistFrecce<T extends string>(
  chiavi: readonly T[],
  attiva: T,
  cambia: (chiave: T) => void,
) {
  const refs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLButtonElement>) => {
      const i = chiavi.indexOf(attiva);
      let prossima: T | null = null;
      if (e.key === 'ArrowRight') prossima = chiavi[(i + 1) % chiavi.length]!;
      else if (e.key === 'ArrowLeft') prossima = chiavi[(i - 1 + chiavi.length) % chiavi.length]!;
      else if (e.key === 'Home') prossima = chiavi[0]!;
      else if (e.key === 'End') prossima = chiavi[chiavi.length - 1]!;
      if (!prossima) return;
      /* Senza, le frecce farebbero anche scorrere la pagina sotto. */
      e.preventDefault();
      cambia(prossima);
      refs.current[prossima]?.focus();
    },
    [chiavi, attiva, cambia],
  );

  /**
   * Gli attributi che ogni bottone-scheda deve portare. `tabIndex` mobile:
   * nella tablist si entra col Tab UNA volta, e da li' ci si muove con le
   * frecce — tre bottoni tutti tabbabili costringono chi usa la tastiera a
   * passarli tutti per uscirne.
   */
  const propsScheda = useCallback(
    (chiave: T, prefisso: string) => ({
      id: `${prefisso}-tab-${chiave}`,
      role: 'tab' as const,
      'aria-selected': attiva === chiave,
      'aria-controls': `${prefisso}-panel-${chiave}`,
      tabIndex: attiva === chiave ? 0 : -1,
      ref: (el: HTMLButtonElement | null) => { refs.current[chiave] = el; },
      onKeyDown,
      type: 'button' as const,
    }),
    [attiva, onKeyDown],
  );

  /** Gli attributi del pannello corrispondente. */
  const propsPannello = useCallback(
    (chiave: T, prefisso: string) => ({
      id: `${prefisso}-panel-${chiave}`,
      role: 'tabpanel' as const,
      'aria-labelledby': `${prefisso}-tab-${chiave}`,
    }),
    [],
  );

  return { propsScheda, propsPannello, onKeyDown, refs };
}
